import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Paper, Stack, Typography, Chip, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import EditView from '../../../views/Edit.view';
import Loading from '../../../components/common/Loading.component';
import Alert from '../../../components/common/Alert.component';
import { fetchAPI } from '../../../api/request';
import { API as handoverAPI } from '../../../module/handover/api';
import { DynamicControl } from '../../../components/form/DynamicControl';
import Divider from '../../../components/form/components/Divider.component';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Button from '../../../components/common/Button.component';
import _ from 'lodash';
import { UpdateConf } from '../../../module/handover/conf/Update.conf';
import RectangleChip from '../../../components/common/RectangleChip.component';

const HandoverEditPage = ({
    id,
    onSuccess = () => {},
}) => {
    const navigate = useNavigate();
    const { jwt, user } = useSelector((state) => state.login);

    const [loadingGet, setLoadingGet] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);

    const userAuth = useSelector((state) => state.login?.user);

    const hasUpdatePermission = useMemo(
        () => userAuth?.permissions?.[_.toUpper('handover')]?.UPDATE === 'true',
        [userAuth]
    );

    const isClosed = useMemo(
        () => _.toUpper(data?.status) === 'CLOSED',
        [data]
    );

    const isEditDisabled = useMemo(
        () => !hasUpdatePermission || isClosed,
        [hasUpdatePermission, isClosed]
    );

    const onError = useCallback((err) => {
        setSuccess(false);
        setLoading(false);
        setError(err);
    }, []);

    const conf = useMemo(
        () => ({
            ...UpdateConf,
            fields: isClosed ? [] : UpdateConf.fields,
        }),
        [isClosed]
    );

    const loadTicket = useCallback(async () => {
        try {
            setLoadingGet(true);
            setError(false);

            const resp = await fetchAPI(handoverAPI.getTicket, { id }, {}, {}, jwt);

            if (!resp?.data?.id) {
                navigate('/404');
                return;
            }

            setData(resp.data);
        } catch (err) {
            onError(err);
            navigate('/404');
        } finally {
            setLoadingGet(false);
        }
    }, [id, jwt, navigate, onError]);

    useEffect(() => {
        if (jwt && id) loadTicket();
    }, [jwt, id, loadTicket]);

    const onSubmit = useCallback(
        async (values) => {
            try {
                if (isClosed) {
                    setError({
                        response: {
                            data: {
                                error: {
                                    message: 'This ticket is closed and cannot be updated.',
                                },
                            },
                        },
                    });
                    return;
                }
                if (!user?.id_users_nautica?.id) {
                    setError({
                        response: {
                            data: {
                                error: {
                                    message: 'No users-nautica associated with the logged user.',
                                },
                            },
                        },
                    });
                    return;
                }

                setLoading(true);
                setSuccess(false);
                setError(false);

                const newNotes = values?.comment?.trim()
                    ? [
                        {
                            comment: values.comment.trim(),
                            created_at_note: new Date().toISOString(),
                            id_user: user.id_users_nautica.id,
                        },
                    ]
                    : [];

                const payload = {
                    type: values?.type?.id || values?.type,
                    priority: values?.priority?.id || values?.priority,
                    status: values?.status?.id || values?.status,
                    notes: newNotes,
                    date: values?.date || null,
                    title: values?.title,
                    description: values?.description,
                    assignee_user_id: values?.assignee_user_id?.id || null,
                };
                await fetchAPI(handoverAPI.updateTicket, { id }, {}, payload, jwt);

                setSuccess(true);
                setLoading(false);
                loadTicket();
                onSuccess();
            } catch (err) {
                onError(err);
            }
        },
        [id, jwt, onError, onSuccess, user, loadTicket, isClosed]
    );

    const onClickDetail = useCallback(() => {
        navigate(`/handover/${id}`);
    }, [navigate, id]);


    return (
        <Container disableGutters maxWidth={false} sx={{ height: '100%' }}>
            <Stack
                spacing={2}
                sx={{ padding: 2 }}
                justifyContent="center"
                alignItems="center"
            >
                <Container>
                    {loading && <Loading />}
                    {success && <Alert severity="success">{'Success updated'}</Alert>}
                    {isClosed && (
                        <Alert severity="info">
                            This ticket is closed. Editing and updates are disabled.
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error">
                            {error?.response?.data?.error?.message
                                ? error?.response?.data?.error?.message
                                : 'An error has occurred'}
                        </Alert>
                    )}

                    {loadingGet ? (
                        <Loading />
                    ) : (
                        <Paper>
                            <Box sx={{ padding: 2 }}>
                                {data && (
                                    <EditView
                                        data={{
                                            ...data,
                                            type: data?.type
                                                ? { id: data.type, label: data.type.toUpperCase() }
                                                : null,
                                            priority: data?.priority
                                                ? { id: data.priority, label: data.priority }
                                                : null,
                                            status: data?.status
                                                ? { id: data.status, label: data.status }
                                                : null,
                                            comment: '',
                                        }}
                                        id={id}
                                        conf={conf}
                                        onEditSubmit={onSubmit}
                                        onEditSuccess={() => setSuccess(true)}
                                        onEditError={onError}
                                        disabled={isEditDisabled}
                                        actions={
                                            <Stack spacing={2} sx={{ mb: 2 }}>
                                                <Stack direction={'row'} justifyContent={'space-between'}>
                                                    <Chip
                                                        label={`ID: ${id}`}
                                                        color='primary'
                                                        variant='outlined'
                                                    />

                                                    <Button
                                                        startIcon={<VisibilityOutlinedIcon />}
                                                        variant='outlined'
                                                        onClick={() => onClickDetail()}
                                                    >
                                                        Ticket Detail
                                                    </Button>
                                                </Stack>
                                                <Divider label="Ticket Information" />
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                    <Grid container spacing={2}>
                                                        {isClosed && <Grid item xs={12} md={6}>
                                                            <RectangleChip
                                                                value={data?.type?.toUpperCase() || '-'}
                                                                backgroundColor="#009ab6"
                                                                color="#fff"
                                                                width="auto"
                                                            />
                                                        </Grid>}
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Yacht</Typography>
                                                            <Typography variant="body1" >{data?.yacht_name || '-'}</Typography>
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Department</Typography>
                                                            <Typography variant="body1">{data?.department || '-'}</Typography>
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Team</Typography>
                                                            <Typography variant="body1">{data?.team || '-'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Created by</Typography>
                                                            <Typography variant="body1">{data?.created_by_user || '-'}</Typography>
                                                        </Grid>
                                                        {isClosed && <Grid item xs={12} md={6}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Closed by</Typography>
                                                            <Typography variant="body1">{data?.closed_by_user || '-'}</Typography>
                                                        </Grid>}
                                                        {isClosed && <>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Priority</Typography>
                                                                <Typography variant="body1"> {data?.priority || '-'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}> Status </Typography>
                                                                <Typography variant="body1">{data?.status || '-'}</Typography>
                                                            </Grid>
                                                        </>}
                                                    </Grid>
                                                </Paper>
                                            </Stack>
                                        }
                                    >
                                        <Stack spacing={2} sx={{ mt: 1 }}>
                                            <Divider label="Notes" />
                                            {data?.notes?.length > 0 && (
                                                <Paper variant="outlined" sx={{ p: 2 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Existing Notes ({data.notes.length})</Typography>

                                                    <Stack spacing={2}>
                                                        {data.notes.map((note, index) => (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    pb: 2,
                                                                    borderBottom:
                                                                        index < data.notes.length - 1
                                                                            ? '1px solid #e0e0e0'
                                                                            : 'none',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{ mb: 1, whiteSpace: 'pre-wrap' }}
                                                                >
                                                                    {note.comment}
                                                                </Typography>

                                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                    <Chip
                                                                        label={
                                                                            note.created_at_note
                                                                                ? dayjs(note.created_at_note).format(
                                                                                    'DD/MM/YYYY HH:mm'
                                                                                )
                                                                                : '-'
                                                                        }
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                    <Chip
                                                                        label={note.user_name || '-'}
                                                                        size="small"
                                                                        color="primary"
                                                                        variant="outlined"
                                                                    />
                                                                </Stack>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Paper>
                                            )}

                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <DynamicControl
                                                        id="comment"
                                                        name="comment"
                                                        type="text_multiline"
                                                        label="Comment"
                                                        disabled={isEditDisabled}
                                                        required={false}
                                                        rows={10}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Stack>
                                    </EditView>
                                )}
                            </Box>
                        </Paper>
                    )}
                </Container>
            </Stack>
        </Container>
    );
};

export default HandoverEditPage;
